import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Search,
  Filter,
  Download,
  MoreHorizontal,
  Eye,
  Trash2,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Loader2,
  FileText,
  AlertTriangle,
  Upload,
  AlertCircle,
  Clock,
  CheckCircle,
  Activity,
  Share2,
  Edit,
} from 'lucide-react';
import { toast } from 'sonner';

const statusConfig = {
  new: { label: 'New', class: 'bg-blue-100 text-blue-700' },
  parsing: { label: 'Parsing', class: 'bg-purple-100 text-purple-700' },
  awaiting_context: { label: 'Awaiting Context', class: 'bg-indigo-100 text-indigo-700' },
  analyzing: { label: 'Analyzing', class: 'bg-cyan-100 text-cyan-700' },
  awaiting_review: { label: 'Awaiting Review', class: 'bg-amber-100 text-amber-700' },
  completed: { label: 'Completed', class: 'bg-green-100 text-green-700' },
  archived: { label: 'Archived', class: 'bg-slate-100 text-slate-700' },
  reopened: { label: 'Reopened', class: 'bg-pink-100 text-pink-700' },
};

const urgencyConfig = {
  critical: { label: 'Critical', class: 'bg-red-100 text-red-700 border-red-200' },
  high: { label: 'High', class: 'bg-orange-100 text-orange-700 border-orange-200' },
  medium: { label: 'Medium', class: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  low: { label: 'Low', class: 'bg-green-100 text-green-700 border-green-200' },
};

export const ReportQueuePage = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [dashboardData, setDashboardData] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // For clinicians, default to showing "My Reports" (assigned + shared with me)
  const [showAllReports, setShowAllReports] = useState(false);
  // Sorting state
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  // Share dialog state
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [reportToShare, setReportToShare] = useState(null);
  const [clinicians, setClinicians] = useState([]);
  const [selectedClinician, setSelectedClinician] = useState('');
  const [shareMessage, setShareMessage] = useState('');
  const [sharing, setSharing] = useState(false);
  const [filters, setFilters] = useState({
    status: searchParams.get('status') || '',
    report_type: searchParams.get('report_type') || '',
    severity: searchParams.get('severity') || '',
    search: '',
  });

  useEffect(() => {
    fetchDashboardData();
    fetchClinicians();
  }, []);

  useEffect(() => {
    fetchReports();
  }, [page, filters, showAllReports, sortBy, sortOrder]);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/dashboard/clinician');
      setDashboardData(response.data);
    } catch (error) {
      console.error('Dashboard error:', error);
    }
  };

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 20);
      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);
      if (filters.status) params.append('status', filters.status);
      if (filters.report_type) params.append('report_type', filters.report_type);
      // For clinicians: pass my_reports=false to see all org reports
      if (showAllReports) params.append('my_reports', 'false');
      
      const response = await api.get(`/reports?${params.toString()}`);
      setReports(response.data.reports || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchClinicians = async () => {
    try {
      const response = await api.get('/users/clinicians?cross_org=true');
      setClinicians(response.data?.clinicians || []);
    } catch (error) {
      console.error('Failed to fetch clinicians:', error);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const handleShareReport = async () => {
    if (!selectedClinician || !reportToShare) return;
    
    setSharing(true);
    try {
      await api.post(`/reports/${reportToShare.report_id}/share`, null, {
        params: {
          target_clinician_id: selectedClinician,
          message: shareMessage || undefined
        }
      });
      toast.success('Report shared successfully');
      setShareDialogOpen(false);
      setReportToShare(null);
      setSelectedClinician('');
      setShareMessage('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to share report');
    } finally {
      setSharing(false);
    }
  };

  const openShareDialog = (report) => {
    setReportToShare(report);
    setSelectedClinician('');
    setShareMessage('');
    setShareDialogOpen(true);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
    if (value) {
      searchParams.set(key, value);
    } else {
      searchParams.delete(key);
    }
    setSearchParams(searchParams);
  };

  const handleDeleteReport = async () => {
    if (!reportToDelete) return;
    
    setDeleting(true);
    try {
      // Archive the report instead of deleting it
      await api.patch(`/reports/${reportToDelete.report_id}`, { status: 'archived' });
      toast.success('Report archived successfully');
      setDeleteDialogOpen(false);
      setReportToDelete(null);
      fetchReports();
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive report');
    } finally {
      setDeleting(false);
    }
  };

  const handleExport = () => {
    toast.success('Export started. File will be downloaded shortly.');
  };

  const totalPages = Math.ceil(total / 20);

  const severityColors = {
    critical: 'bg-red-50 border-red-200 text-red-700',
    high: 'bg-orange-50 border-orange-200 text-orange-700',
    medium: 'bg-yellow-50 border-yellow-200 text-yellow-700',
    low: 'bg-green-50 border-green-200 text-green-700',
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="report-queue-page">
      {/* Header with Welcome Message */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
            Welcome back, {user?.full_name?.split(' ')[0] || 'User'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {total} reports total &bull; Here&apos;s what needs your attention
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExport} data-testid="export-btn">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button asChild data-testid="upload-report-btn">
            <Link to="/reports/upload">
              <Upload className="w-4 h-4 mr-2" />
              Upload Report
            </Link>
          </Button>
        </div>
      </div>

      {/* Dashboard Stats */}
      {dashboardData && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card 
            className="cursor-pointer hover:border-primary/50 transition-colors" 
            onClick={() => handleFilterChange('status', 'awaiting_review')}
            data-testid="stat-awaiting-review"
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground">Awaiting Review</p>
                  <p className="text-2xl font-bold text-slate-900">{dashboardData?.awaiting_review || 0}</p>
                </div>
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:border-primary/50 transition-colors"
            onClick={() => handleFilterChange('severity', 'critical')}
            data-testid="stat-critical"
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground">Critical Alerts</p>
                  <p className="text-2xl font-bold text-red-600">{dashboardData?.severity_breakdown?.critical || 0}</p>
                </div>
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:border-primary/50 transition-colors"
            onClick={() => handleFilterChange('status', 'completed')}
            data-testid="stat-completed"
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground">Completed Today</p>
                  <p className="text-2xl font-bold text-green-600">{dashboardData?.completed_today || 0}</p>
                </div>
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-processing">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground">Processing</p>
                  <p className="text-2xl font-bold text-blue-600">{dashboardData?.processing || 0}</p>
                </div>
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Activity className="w-5 h-5 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by patient name, MRN, or report ID..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            {/* Report Scope Toggle for Clinicians */}
            {user?.role === 'clinician' && (
              <Select value={showAllReports ? "all" : "mine"} onValueChange={(v) => {
                setShowAllReports(v === "all");
                setPage(1);
              }}>
                <SelectTrigger className="w-full sm:w-40" data-testid="scope-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Reports</SelectItem>
                  <SelectItem value="mine">My Reports</SelectItem>
                </SelectContent>
              </Select>
            )}
            <Select value={filters.status || "all"} onValueChange={(v) => handleFilterChange('status', v === 'all' ? '' : v)}>
              <SelectTrigger className="w-full sm:w-40" data-testid="status-filter">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {Object.entries(statusConfig).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.report_type || "all"} onValueChange={(v) => handleFilterChange('report_type', v === 'all' ? '' : v)}>
              <SelectTrigger className="w-full sm:w-40" data-testid="type-filter">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="laboratory">Laboratory</SelectItem>
                <SelectItem value="radiology">Radiology</SelectItem>
                <SelectItem value="pathology">Pathology</SelectItem>
                <SelectItem value="cardiology">Cardiology</SelectItem>
              </SelectContent>
            </Select>
            {(filters.status || filters.report_type || filters.severity) && (
              <Button 
                variant="ghost" 
                onClick={() => {
                  setFilters({ status: '', report_type: '', severity: '', search: '' });
                  setSearchParams({});
                }}
              >
                Clear Filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Reports Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : reports.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <FileText className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-lg font-medium text-slate-900">No reports found</p>
              <p className="text-sm text-muted-foreground mt-1">
                {filters.status || filters.report_type ? 'Try adjusting your filters' : 'Upload a report to get started'}
              </p>
              <Button asChild className="mt-4">
                <Link to="/reports/upload">
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Report
                </Link>
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead 
                    className="cursor-pointer hover:bg-slate-100 select-none"
                    onClick={() => handleSort('report_id')}
                  >
                    <div className="flex items-center gap-1">
                      Report ID
                      {sortBy === 'report_id' && (
                        sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-slate-100 select-none"
                    onClick={() => handleSort('patient_name')}
                  >
                    <div className="flex items-center gap-1">
                      Patient
                      {sortBy === 'patient_name' && (
                        sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead>MRN</TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-slate-100 select-none"
                    onClick={() => handleSort('dept_name')}
                  >
                    <div className="flex items-center gap-1">
                      Department
                      {sortBy === 'dept_name' && (
                        sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-slate-100 select-none"
                    onClick={() => handleSort('report_type')}
                  >
                    <div className="flex items-center gap-1">
                      Type
                      {sortBy === 'report_type' && (
                        sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-slate-100 select-none"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center gap-1">
                      Status
                      {sortBy === 'status' && (
                        sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-slate-100 select-none"
                    onClick={() => handleSort('urgency_level')}
                  >
                    <div className="flex items-center gap-1">
                      Urgency
                      {sortBy === 'urgency_level' && (
                        sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead>Sharing</TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-slate-100 select-none"
                    onClick={() => handleSort('created_at')}
                  >
                    <div className="flex items-center gap-1">
                      Date
                      {sortBy === 'created_at' && (
                        sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reports.map((report) => (
                  <TableRow 
                    key={report.report_id} 
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => navigate(`/reports/${report.report_id}`)}
                    data-testid="report-row"
                  >
                    <TableCell className="font-mono text-sm">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-muted-foreground" />
                        {report.report_id?.slice(0, 8)}...
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="font-medium">{report.patient_name || 'Unknown'}</p>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm font-mono text-slate-600">{report.mrn || '-'}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-600">
                        {report.dept_name || '-'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {report.report_type?.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={statusConfig[report.status]?.class}>
                        {statusConfig[report.status]?.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {report.urgency_level ? (
                        <Badge className={`${urgencyConfig[report.urgency_level]?.class} border`}>
                          {report.urgency_level === 'critical' && <AlertTriangle className="w-3 h-3 mr-1" />}
                          {urgencyConfig[report.urgency_level]?.label}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {/* Check if this report was shared with the current user */}
                      {report.shared_with?.some(share => share.user_id === user?.user_id) ? (
                        <div className="flex items-center gap-1">
                          <Share2 className="w-3 h-3 text-green-500" />
                          <span className="text-xs text-green-600">
                            from {(() => {
                              const shareEntry = report.shared_with.find(s => s.user_id === user?.user_id);
                              return shareEntry?.shared_by_name || report.assigned_clinician_name || 'colleague';
                            })()}
                          </span>
                        </div>
                      ) : report.shared_with && report.shared_with.length > 0 ? (
                        <div className="flex flex-col gap-1">
                          {report.shared_with.map((share, idx) => (
                            <div key={idx} className="flex items-center gap-1">
                              <Share2 className="w-3 h-3 text-blue-500" />
                              <span className="text-xs text-blue-600">to {share.full_name}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-xs">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(report.report_date).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="sm" data-testid="report-actions">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/reports/${report.report_id}`);
                          }}>
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/reports/${report.report_id}/edit`);
                          }}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          {['clinician', 'org_admin', 'super_admin', 'admin'].includes(user?.role) && (
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              openShareDialog(report);
                            }}>
                              <Share2 className="w-4 h-4 mr-2" />
                              Share with Doctor
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            className="text-red-600 focus:text-red-600"
                            onClick={(e) => {
                              e.stopPropagation();
                              setReportToDelete(report);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete Report
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <Card className="mt-4 mb-24">
          <CardContent className="py-3 px-4">
            <div className="flex items-center justify-between flex-wrap gap-3" data-testid="pagination-controls">
              <p className="text-sm text-muted-foreground">
                Showing {((page - 1) * 20) + 1} to {Math.min(page * 20, total)} of {total} reports
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </Button>
                <span className="text-sm font-medium px-3 py-1 bg-slate-100 rounded">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  data-testid="next-page-btn"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
                <div className="flex items-center gap-1 ml-2">
                  <span className="text-sm text-muted-foreground">Go to:</span>
                  <Input
                    type="number"
                    min="1"
                    max={totalPages}
                    placeholder="Page"
                    className="w-20 h-8 text-sm"
                    data-testid="goto-page-input"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const pageNum = parseInt(e.target.value);
                        if (pageNum >= 1 && pageNum <= totalPages) {
                          setPage(pageNum);
                          e.target.value = '';
                        } else {
                          toast.error(`Please enter a page number between 1 and ${totalPages}`);
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Report?</AlertDialogTitle>
            <AlertDialogDescription>
              This will Archive the report.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteReport}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Archiving...
                </>
              ) : (
                'Continue'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Share Report Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Share Report</DialogTitle>
            <DialogDescription>
              Share this report with another doctor. They will receive a notification.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Patient</label>
              <p className="text-sm text-muted-foreground">{reportToShare?.patient_name || 'Unknown'}</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Select Doctor</label>
              <Select value={selectedClinician} onValueChange={setSelectedClinician}>
                <SelectTrigger data-testid="share-clinician-select">
                  <SelectValue placeholder="Choose a doctor to share with" />
                </SelectTrigger>
                <SelectContent>
                  {clinicians
                    .filter(c => c.user_id !== user?.user_id)
                    .map((clinician) => (
                      <SelectItem key={clinician.user_id} value={clinician.user_id}>
                        {clinician.full_name} ({clinician.org_name})
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Message (optional)</label>
              <Input
                placeholder="Add a note for the recipient..."
                value={shareMessage}
                onChange={(e) => setShareMessage(e.target.value)}
                data-testid="share-message-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShareDialogOpen(false)} disabled={sharing}>
              Cancel
            </Button>
            <Button onClick={handleShareReport} disabled={!selectedClinician || sharing}>
              {sharing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sharing...
                </>
              ) : (
                <>
                  <Share2 className="w-4 h-4 mr-2" />
                  Share Report
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
